import { rankByNetworkSimplex, type Constraint } from "./network-simplex";

/**
 * Places the items of a retained Local Map exploration on the vertical axis:
 * the cards and lanes of every column, in the order the sweep chose, so that
 * the wires between pins are as short as the constraints allow, by the exact
 * method of Gansner, Koutsofios, North and Vo. The objective is the weighted
 * sum of the vertical distances between the two pins of every wire, a
 * bundle's shared run weighing its member count. The constraints are the
 * order within each column with a gap between neighbours, each directory's
 * membrane around its members with its label and padding, and sibling
 * directories that share a column standing in their rows there without
 * interleaving.
 *
 * A directory is a membrane: one segment per column it spans, each around
 * that column's members and the segments its subdirectories have there, at
 * its own height, so the shape follows its members from column to column
 * instead of boxing them all into one rectangle. Segments in neighbouring
 * columns overlap by at least the neck, and so stand at least that tall, so a
 * membrane is one connected shape and never crosses a sibling's. The label's
 * room is in the leftmost segment.
 *
 * A lane is a box around its slots, one per bundle, which stand in the order
 * the sweep chose, each at least its own height below the one above. A slot
 * is an item like a card, so the wires through it place it, and the lane's
 * edges follow its first and last slots at the lane's padding: a lane is as
 * tall as its wires ask and no taller, and its heavy slots do not decide
 * where its light ones go.
 *
 * Beneath the wires, every box is pulled tight around its members: a box's
 * height costs one unit against a wire's million, so no box is taller than
 * its members and the wires need, and the wires are never traded for it.
 *
 * Two shapes may be priced beside the wires, each by a weight in units of a
 * one-reference wire (the owner's ask of 2026-10-07, that a membrane be
 * rewarded for rectangularity and its cards for standing level): the
 * evenness weight charges every pixel of step between a membrane's
 * neighbouring segments, top and bottom, so that at a high weight the outline
 * is the rectangle around its members, stretched rather than moving a card
 * since a box's height is nearly free; and the levelness weight charges every
 * pixel between the tops of the k-th cards of a membrane's neighbouring
 * columns, which does move cards, the wires paying where the weight says so.
 * Both are auxiliary nodes of the same kind a wire is. At zero, neither
 * exists and the picture is the wires' alone.
 *
 * Pure-function module: no DOM. Heights, offsets and gaps are CSS pixels of
 * the unscaled page, rounded to integers; the renderer measures them before
 * placing and reads the positions back as absolute tops.
 *
 * @module branch-placement
 */

/** A place on an item where a wire ends: the item and the distance from its top. */
export interface Pin {
  item: string;
  offset: number;
}

/** A wire between two pins, weighted by how many references it draws. */
export interface PlacementWire {
  from: Pin;
  to: Pin;
  weight: number;
}

/**
 * A directory's membrane: its members, its subdirectories, the room its label and padding take, and its place
 * among its siblings. It has one segment per column from `minColumn` to `maxColumn`.
 */
export interface PlacementBand {
  key: string;
  /** The room between the leftmost segment's top edge and its first member: label and padding; zero for a box drawn as nothing. */
  insetTop: number;
  /** The room between a member and the segment's edge everywhere else: below every segment and above all but the leftmost. */
  insetBottom: number;
  minColumn: number;
  maxColumn: number;
  /** The membrane's row among its siblings; where a sibling in a lower row shares a column, its segment there stands below. */
  row: number;
  /** Whether the box has a drawn outline whose shape is priced: a membrane, not the picture's root nor a lane. */
  shaped: boolean;
  /** The items directly in this membrane, each in one of its columns. */
  items: readonly string[];
  /** Cards paired by the row-levelness objective; defaults to `items`. May include cards in this directory's invisible file buckets without changing their containment. */
  rowItems?: readonly string[];
  children: readonly PlacementBand[];
}

/** One column's part of a membrane: its edges there. */
export interface Segment {
  column: number;
  top: number;
  bottom: number;
}

/** A lane in a column's stack: a box of `bands`, whose items are the lane's slots and whose insets are its padding. */
export interface PlacementLane {
  /** The box's key, as `bands` names it. */
  key: string;
  /** The slots, top to bottom, one per bundle: items of the lane's pitch in height, each standing at least that far below the one above. */
  slots: readonly string[];
}

/** One thing in a column's stack: a card by its item id, or a lane. */
export type StackEntry = string | PlacementLane;

/** One membrane edge held level across an inclusive, contiguous interval of columns. */
export interface BoundaryAlignment {
  band: string;
  edge: "top" | "bottom";
  fromColumn: number;
  toColumn: number;
}

/** What the placement takes. */
export interface PlacementInput {
  /** The cards and lanes of each column, top to bottom. */
  columns: readonly (readonly StackEntry[])[];
  /** Every item's height, slots included. */
  heights: ReadonlyMap<string, number>;
  wires: readonly PlacementWire[];
  /** The top-level boxes. */
  bands: readonly PlacementBand[];
  /** Explicit level runs for an alignment candidate; overlapping intervals on the same edge form one run. */
  alignments?: readonly BoundaryAlignment[];
  /** The least room between neighbouring cards and lanes of a column. */
  gap: number;
  /** The least room between sibling membranes' segments in a column they share. */
  bandGap: number;
  /** The least overlap of one membrane's segments in neighbouring columns, so that it is one connected shape. */
  neck: number;
  /** What a pixel of step between a membrane's neighbouring segments costs, in units of a one-reference wire's pixel; zero, or absent, leaves the outline to its members. */
  evenness?: number;
  /** What a pixel between the tops of the k-th cards of a membrane's neighbouring columns costs, in the same units; zero, or absent, leaves the cards to their wires. */
  levelness?: number;
}

/** The placement: every item's top, every membrane's segments, and the objective. */
export interface Placement {
  top: Map<string, number>;
  /** Each membrane's segments, left to right, one per column it spans. */
  boxes: Map<string, Segment[]>;
  /** The weighted sum of the wires' vertical distances. */
  cost: number;
  optimal: boolean;
}

/** What a unit of a wire's vertical distance costs against a unit of a box's height. */
const WIRE_WEIGHT = 1_000_000;

const boundaryKey = (edge: BoundaryAlignment["edge"], band: string, column: number): string => `${edge}\0${band}\0${column}`;

/**
 * Canonical node names for explicitly level runs. Aliasing keeps their edges equal without adding the two opposing
 * constraints that would make a cycle. Only overlapping intervals join; adjacent, non-overlapping runs stay separate.
 */
function boundaryAliases(input: PlacementInput): Map<string, string> {
  const aliases = new Map<string, string>();
  if (!input.alignments?.length) return aliases;
  const bands = new Map<string, PlacementBand>();
  const collect = (children: readonly PlacementBand[]): void => {
    for (const band of children) { bands.set(band.key, band); collect(band.children); }
  };
  collect(input.bands);
  const groups = new Map<string, BoundaryAlignment[]>();
  for (const alignment of input.alignments) {
    const { band: key, edge, fromColumn, toColumn } = alignment;
    const band = bands.get(key);
    if (!band) throw new Error(`The boundary alignment references unknown membrane ${JSON.stringify(key)}.`);
    if (edge !== "top" && edge !== "bottom") throw new Error(`The boundary alignment of ${JSON.stringify(key)} has invalid edge ${JSON.stringify(edge)}; expected top or bottom.`);
    if (!Number.isInteger(fromColumn) || !Number.isInteger(toColumn) || fromColumn > toColumn) {
      throw new Error(`The ${edge} alignment of ${JSON.stringify(key)} needs an increasing inclusive interval of integer columns; received ${fromColumn}..${toColumn}.`);
    }
    if (fromColumn < band.minColumn || toColumn > band.maxColumn || fromColumn < 0 || toColumn >= input.columns.length) {
      throw new Error(`The ${edge} alignment ${fromColumn}..${toColumn} reaches outside the columns of membrane ${JSON.stringify(key)} (${band.minColumn}..${band.maxColumn}) or the picture.`);
    }
    const groupKey = `${edge}\0${key}`;
    const group = groups.get(groupKey);
    if (group) group.push(alignment);
    else groups.set(groupKey, [alignment]);
  }
  for (const group of groups.values()) {
    group.sort((a, b) => a.fromColumn - b.fromColumn || a.toColumn - b.toColumn);
    const { band, edge } = group[0];
    let start = group[0].fromColumn, end = group[0].toColumn;
    for (let i = 1; i <= group.length; i++) {
      const next = group[i];
      if (next && next.fromColumn <= end) { end = Math.max(end, next.toColumn); continue; }
      for (let column = start + 1; column <= end; column++) aliases.set(boundaryKey(edge, band, column), boundaryKey(edge, band, start));
      if (next) { start = next.fromColumn; end = next.toColumn; }
    }
  }
  return aliases;
}

/** The weighted sum of the wires' vertical distances at given tops; a wire whose pin is unplaced costs nothing. */
export function placementCost(wires: readonly PlacementWire[], top: ReadonlyMap<string, number>): number {
  let cost = 0;
  for (const wire of wires) {
    const a = top.get(wire.from.item), b = top.get(wire.to.item);
    if (a === undefined || b === undefined) continue;
    cost += wire.weight * Math.abs(a + wire.from.offset - (b + wire.to.offset));
  }
  return cost;
}

/** Places the items; see the module note. */
export function placeBranches(input: PlacementInput): Placement {
  const aliases = boundaryAliases(input);
  const index = new Map<string, number>();
  let count = 0;
  const node = (key: string): number => index.get(key) ?? (index.set(key, count), count++);
  const constraints: Constraint[] = [];
  const itemNode = (id: string): number => node(`item\0${id}`);
  const height = (id: string): number => Math.round(input.heights.get(id) ?? 0);
  const segmentNode = (edge: BoundaryAlignment["edge"], key: string, column: number): number => {
    const name = boundaryKey(edge, key, column);
    return node(aliases.get(name) ?? name);
  };
  const segmentTop = (key: string, column: number): number => segmentNode("top", key, column);
  const segmentBottom = (key: string, column: number): number => segmentNode("bottom", key, column);

  // Every item's column, from the stacks: a card's directly, a slot's through its lane.
  const columnOf = new Map<string, number>();
  input.columns.forEach((column, index) => {
    for (const entry of column) {
      if (typeof entry === "string") columnOf.set(entry, index);
      else for (const slot of entry.slots) columnOf.set(slot, index);
    }
  });

  // A column's stack: each entry's top edge the gap below the edge above it; a card's edges are its top and its
  // top plus its height, a lane's the edges of its segment. Within a lane the slots stand in order, each touching the one above.
  const lanes: PlacementLane[] = [];
  input.columns.forEach((column, index) => {
    const edges = (entry: StackEntry): { top: number; bottom: number; height: number } =>
      typeof entry === "string" ? { top: itemNode(entry), bottom: itemNode(entry), height: height(entry) } : { top: segmentTop(entry.key, index), bottom: segmentBottom(entry.key, index), height: 0 };
    for (let i = 1; i < column.length; i++) {
      const above = edges(column[i - 1]), below = edges(column[i]);
      constraints.push({ tail: above.bottom, head: below.top, delta: above.height + Math.round(input.gap), weight: 0 });
    }
    for (const entry of column) {
      if (typeof entry === "string") continue;
      lanes.push(entry);
      for (let i = 1; i < entry.slots.length; i++) {
        constraints.push({ tail: itemNode(entry.slots[i - 1]), head: itemNode(entry.slots[i]), delta: height(entry.slots[i - 1]), weight: 0 });
      }
    }
  });

  const spans = new Map<string, PlacementBand>();
  const even = Math.round((input.evenness ?? 0) * WIRE_WEIGHT), level = Math.round((input.levelness ?? 0) * WIRE_WEIGHT);
  const visit = (bands: readonly PlacementBand[]): void => {
    for (const band of bands) {
      spans.set(band.key, band);
      const insetTop = Math.round(band.insetTop), insetBottom = Math.round(band.insetBottom);
      // The room above a segment's members: the label's in the leftmost column, the padding's elsewhere.
      const above = (column: number): number => (column === band.minColumn ? insetTop : insetBottom);
      for (const item of band.items) {
        const column = columnOf.get(item);
        if (column === undefined || column < band.minColumn || column > band.maxColumn) {
          throw new Error(`The item ${JSON.stringify(item)} of ${JSON.stringify(band.key)} stands in no column of that membrane.`);
        }
        constraints.push({ tail: segmentTop(band.key, column), head: itemNode(item), delta: above(column), weight: 0 });
        constraints.push({ tail: itemNode(item), head: segmentBottom(band.key, column), delta: height(item) + insetBottom, weight: 0 });
      }
      for (const child of band.children) {
        if (child.minColumn < band.minColumn || child.maxColumn > band.maxColumn) {
          throw new Error(`The membrane ${JSON.stringify(child.key)} reaches outside ${JSON.stringify(band.key)}, which holds it.`);
        }
        for (let column = child.minColumn; column <= child.maxColumn; column++) {
          constraints.push({ tail: segmentTop(band.key, column), head: segmentTop(child.key, column), delta: above(column), weight: 0 });
          constraints.push({ tail: segmentBottom(child.key, column), head: segmentBottom(band.key, column), delta: insetBottom, weight: 0 });
        }
      }
      // Only a drawn membrane needs a connected outline. The invisible picture root must not tie unrelated columns
      // together merely to overlap an outline that does not exist.
      const neck = band.shaped && band.minColumn < band.maxColumn ? Math.round(input.neck) : 0;
      for (let column = band.minColumn; column <= band.maxColumn; column++) {
        // Each segment as tight as its members allow, and no shorter than the neck it must share: its height costs, far beneath any wire.
        constraints.push({ tail: segmentTop(band.key, column), head: segmentBottom(band.key, column), delta: neck, weight: 1 });
        // Neighbouring segments overlap by the neck, so the membrane is one shape.
        if (band.shaped && column > band.minColumn) {
          constraints.push({ tail: segmentTop(band.key, column - 1), head: segmentBottom(band.key, column), delta: neck, weight: 0 });
          constraints.push({ tail: segmentTop(band.key, column), head: segmentBottom(band.key, column - 1), delta: neck, weight: 0 });
        }
      }
      // The shape's price, where a weight asks: a step between neighbouring segments' tops or bottoms costs the evenness
      // weight per pixel, and the k-th cards of neighbouring columns cost the levelness weight per pixel between their
      // tops, each through an auxiliary node both stay above, as a wire's pins do (the cost is then the distance).
      if (band.shaped && (even > 0 || level > 0)) {
        const members = new Set(band.rowItems ?? band.items);
        const cardsOf = (column: number): string[] => input.columns[column].filter((entry): entry is string => typeof entry === "string" && members.has(entry));
        for (let column = band.minColumn + 1; column <= band.maxColumn; column++) {
          if (even > 0) {
            for (const [edge, name] of [[segmentTop, "top"], [segmentBottom, "bottom"]] as const) {
              const aux = node(`even\0${band.key}\0${column}\0${name}`);
              constraints.push({ tail: aux, head: edge(band.key, column - 1), delta: 0, weight: even });
              constraints.push({ tail: aux, head: edge(band.key, column), delta: 0, weight: even });
            }
          }
          if (level > 0) {
            const left = cardsOf(column - 1), right = cardsOf(column);
            for (let k = 0; k < Math.min(left.length, right.length); k++) {
              const aux = node(`level\0${band.key}\0${column}\0${k}`);
              constraints.push({ tail: aux, head: itemNode(left[k]), delta: 0, weight: level });
              constraints.push({ tail: aux, head: itemNode(right[k]), delta: 0, weight: level });
            }
          }
        }
      }
      visit(band.children);
    }
    // Siblings stand in their rows in every column they share, one segment wholly above the other.
    for (const upper of bands) {
      for (const lower of bands) {
        if (upper === lower || upper.row >= lower.row) continue;
        for (let column = Math.max(upper.minColumn, lower.minColumn); column <= Math.min(upper.maxColumn, lower.maxColumn); column++) {
          constraints.push({ tail: segmentBottom(upper.key, column), head: segmentTop(lower.key, column), delta: Math.round(input.bandGap), weight: 0 });
        }
      }
    }
  };
  visit(input.bands);
  for (const lane of lanes) {
    const span = spans.get(lane.key);
    const column = columnOf.get(lane.slots[0] ?? "");
    if (!span || column === undefined || span.minColumn !== column || span.maxColumn !== column) {
      throw new Error(`The lane ${JSON.stringify(lane.key)} stands in a column but is no single-column box of the bands there, so its edges would float free of its slots.`);
    }
  }

  // A wire costs its vertical distance: an auxiliary node both pins stay above, each by its offset, so the
  // cheapest place for it is the lower pin and the cost is the distance to the other (Gansner et al., section 4.2).
  for (const wire of input.wires) {
    if (!input.heights.has(wire.from.item) || !input.heights.has(wire.to.item)) continue;
    const aux = node(`wire\0${constraints.length}`);
    constraints.push({ tail: aux, head: itemNode(wire.from.item), delta: -Math.round(wire.from.offset), weight: wire.weight * WIRE_WEIGHT });
    constraints.push({ tail: aux, head: itemNode(wire.to.item), delta: -Math.round(wire.to.offset), weight: wire.weight * WIRE_WEIGHT });
  }
  for (const id of input.heights.keys()) itemNode(id);

  const ranking = rankByNetworkSimplex(count, constraints);
  // Disconnected constraint components have no relative vertical meaning. Align their content origins without
  // changing any constraint or objective: every wire, lane and real membrane stays inside its own component.
  const parents = Array.from({ length: count }, (_, i) => i);
  const component = (id: number): number => {
    while (parents[id] !== id) { parents[id] = parents[parents[id]]; id = parents[id]; }
    return id;
  };
  for (const edge of constraints) parents[component(edge.head)] = component(edge.tail);
  const origins = new Map<number, number>();
  const include = (id: number): void => {
    const root = component(id);
    origins.set(root, Math.min(origins.get(root) ?? Infinity, ranking.position[id]));
  };
  for (const id of input.heights.keys()) include(itemNode(id));
  for (const band of spans.values()) for (let column = band.minColumn; column <= band.maxColumn; column++) include(segmentTop(band.key, column));
  for (let id = 0; id < count; id++) ranking.position[id] -= origins.get(component(id)) ?? 0;
  const top = new Map<string, number>();
  for (const id of input.heights.keys()) top.set(id, ranking.position[itemNode(id)]);
  const boxes = new Map<string, Segment[]>();
  const collect = (bands: readonly PlacementBand[]): void => {
    for (const band of bands) {
      const segments: Segment[] = [];
      for (let column = band.minColumn; column <= band.maxColumn; column++) {
        segments.push({ column, top: ranking.position[segmentTop(band.key, column)], bottom: ranking.position[segmentBottom(band.key, column)] });
      }
      boxes.set(band.key, segments);
      collect(band.children);
    }
  };
  collect(input.bands);
  // The picture starts at zero: the least top of any item or segment.
  let least = Infinity;
  for (const value of top.values()) least = Math.min(least, value);
  for (const segments of boxes.values()) for (const segment of segments) least = Math.min(least, segment.top);
  if (Number.isFinite(least) && least !== 0) {
    for (const [id, value] of top) top.set(id, value - least);
    for (const segments of boxes.values()) for (const segment of segments) { segment.top -= least; segment.bottom -= least; }
  }
  return { top, boxes, cost: placementCost(input.wires, top), optimal: ranking.optimal };
}
